import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { HiArrowRight, HiArrowUpRight } from "react-icons/hi2";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import "../css/main.scss";

const collections = [
  { title: "Between seasons", note: "A lighter kind of layering", image: "editorial-01", category: "New Arrivals" },
  { title: "The finishing touch", note: "Caps with a little character", image: "editorial-02", category: "Accessories" },
  { title: "Out & about", note: "Everyday outer layers", image: "editorial-03", category: "Jackets" },
  { title: "Easy in cotton", note: "Your starting point", image: "editorial-04", category: "Heavy Duty Cotton" },
  { title: "A softer outlook", note: "Texture to settle into", image: "editorial-05", category: "Sweaters" },
  { title: "One more layer", note: "Overshirts, your way", image: "editorial-06", category: "Shirts" },
];

export default function Main() {
  useEffect(() => { document.title = `${siteConfig.brandName} | Everyday Layers`; }, []);
  const toTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const money = (price) => new Intl.NumberFormat(siteConfig.currency.locale, { style: "currency", currency: siteConfig.currency.code }).format(price);

  return (
    <main className="home-page">
      <section className="home-hero" aria-labelledby="home-hero-title">
        <div className="home-hero__content">
          <p className="eyebrow"><span className="tiny-star" aria-hidden="true">✳</span> THE EVERYDAY EDIT</p>
          <h1 id="home-hero-title">A little texture.<br />A little <em>you.</em></h1>
          <p className="home-hero__description">Easy layers, familiar comforts. Pieces that find their own place in your everyday.</p>
          <Link className="sc-button" to="/shop" onClick={toTop}>Find your everyday <HiArrowRight aria-hidden="true" /></Link>
          <div className="home-hero__footnote"><span>01 / SUDDLECORNER</span><span>Take a slower look.</span></div>
        </div>
        <div className="home-hero__visual">
          <figure className="home-hero__portrait">
            <img src="/img/editorial/hero-left.jpg" alt="An everyday olive jacket styled with relaxed denim" width="1122" height="1402" fetchpriority="high" />
            <figcaption>Made for your kind of day.</figcaption>
          </figure>
          <figure className="home-hero__detail">
            <img src="/img/editorial/hero-right.jpg" alt="Rich red knit texture and an easy cardigan silhouette" width="1122" height="1402" />
            <figcaption>A closer look at comfort <HiArrowUpRight aria-hidden="true" /></figcaption>
          </figure>
          <span className="home-hero__side-note" aria-hidden="true">TEXTURE / COLOUR / EVERYDAY</span>
        </div>
      </section>

      <section className="home-intro-strip" aria-label="Our perspective">
        <p>A wardrobe, <em>with room for you.</em></p>
        <span>Considered textures</span><span>Easy combinations</span><span>Everyday character</span>
      </section>

      <section className="home-products sc-section" aria-labelledby="home-products-title">
        <div className="home-section-heading">
          <div><p className="eyebrow">MEET YOUR NEXT FAVOURITE</p><h2 id="home-products-title">Fresh in the corner.</h2></div>
          <Link className="sc-text-link" to="/shop?category=New%20Arrivals" onClick={toTop}>Explore new arrivals <HiArrowUpRight aria-hidden="true" /></Link>
        </div>
        <div className="home-products__grid">
          {productData.slice(0, 4).map((product) => (
            <Link className="home-product" key={product.id} to={`/shopDetails/${product.id}`} onClick={toTop}>
              <div className="home-products__image">
                {product.isNew && <span>Just in</span>}
                <img src={`/img/editorial/home-${String(product.id).padStart(2, "0")}.jpg`} alt={product.name} width="1122" height="1402" loading="lazy" decoding="async" />
                <span className="home-product__arrow" aria-hidden="true"><HiArrowUpRight /></span>
              </div>
              <p className="home-product__category">{product.category}</p>
              <div className="home-product__details"><h3>{product.name}</h3><p>{money(product.price)}</p></div>
            </Link>
          ))}
        </div>
      </section>

      <section className="home-collections sc-section" aria-labelledby="collections-title">
        <div className="home-section-heading"><div><p className="eyebrow">FIND YOUR OWN RHYTHM</p><h2 id="collections-title">Different days.<br /><em>All your own.</em></h2></div><p className="home-section-note">From the first layer to the finishing touch.<br />A few places to begin.</p></div>
        <div className="home-stories">
          {collections.map((story, index) => (
            <Link className="home-story" key={story.title} to={`/shop?category=${encodeURIComponent(story.category)}`} onClick={toTop}>
              <div className="home-story__media"><img src={`/img/editorial/${story.image}.jpg`} alt={`${story.category} collection styling`} width="1122" height="1402" loading="lazy" decoding="async" /><span>{String(index + 1).padStart(2, "0")}</span></div>
              <div className="home-story__copy"><div><p>{story.note}</p><h3>{story.title}</h3></div><HiArrowUpRight aria-hidden="true" /></div>
            </Link>
          ))}
        </div>
      </section>

      <section className="home-history" aria-labelledby="home-history-title">
        <div className="home-history__image"><img src="/img/editorial/history.jpg" alt="Folded everyday layers and fabric swatches in a sunlit studio" width="1536" height="1024" loading="lazy" decoding="async" /></div>
        <div className="home-history__copy"><p className="eyebrow">A NOTE FROM OUR CORNER</p><span className="home-history__symbol" aria-hidden="true">✳</span><h2 id="home-history-title">Good things.<br /><em>In everyday rotation.</em></h2><p>{siteConfig.description}</p><Link className="sc-text-link" to="/about" onClick={toTop}>Get to know suddlecorner <HiArrowUpRight aria-hidden="true" /></Link></div>
      </section>

      <section className="home-factory sc-section" aria-labelledby="home-factory-title">
        <div className="home-factory__copy"><p className="eyebrow">THE DETAILS MAKE THE DIFFERENCE</p><h2 id="home-factory-title">Something you<br />can <em>feel.</em></h2><p>A woven surface. A soft knit. A useful pocket. Discover the little details that give everyday pieces their character.</p><Link className="sc-text-link" to="/about#factory">Our approach <HiArrowUpRight aria-hidden="true" /></Link></div>
        <figure><img src="/img/editorial/factory.jpg" alt="Illustrative close view of cotton fabric and seam details" width="1536" height="1024" loading="lazy" decoding="async" /><figcaption>LOOK CLOSER / A STUDY IN TEXTURE</figcaption></figure>
      </section>
    </main>
  );
}
